/**
 * i18n Configuration for SettleWise
 *
 * Internationalization setup using react-i18next with:
 * - Subdirectory-based URL routing (/es/pricing)
 * - Language detection from URL path, localStorage, browser
 * - Lazy-loaded translation files by namespace
 */

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Supported languages - add new languages here
export const SUPPORTED_LOCALES = ['en', 'es', 'zh'] as const;
export type SupportedLocale = typeof SUPPORTED_LOCALES[number];
export const DEFAULT_LOCALE: SupportedLocale = 'en';

// Language metadata for UI
export const LANGUAGES = [
  { code: 'en' as const, name: 'English', nativeName: 'English', flag: '🇺🇸' },
  { code: 'es' as const, name: 'Spanish', nativeName: 'Español', flag: '🇲🇽' },
  { code: 'zh' as const, name: 'Chinese', nativeName: '中文', flag: '🇨🇳' },
  // Future languages:
  // { code: 'vi' as const, name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳' },
] as const;

// Namespaces for translation files
export const NAMESPACES = [
  'common',      // Shared UI: buttons, errors, validation
  'navigation',  // Sidebar, header, footer
  'marketing',   // Landing pages, pricing
  'auth',        // Login, signup, password flows
  'app',         // Dashboard, main app features
  'legal',       // Disclaimers, terms
] as const;

// Custom language detector for URL path
const pathDetector = {
  name: 'path',
  lookup(): string | undefined {
    if (typeof window === 'undefined') return undefined;

    const pathParts = window.location.pathname.split('/').filter(Boolean);
    const firstPart = pathParts[0];

    if (firstPart && SUPPORTED_LOCALES.includes(firstPart as SupportedLocale)) {
      return firstPart;
    }
    return undefined;
  },
  cacheUserLanguage(lng: string): void {
    if (typeof window !== 'undefined') {
      localStorage.setItem('settlewise_language', lng);
    }
  },
};

// Create LanguageDetector instance and add custom detector BEFORE init
const languageDetector = new LanguageDetector();
languageDetector.addDetector(pathDetector);

// Initialize i18next
i18n
  .use(HttpBackend)
  .use(languageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: DEFAULT_LOCALE,
    supportedLngs: [...SUPPORTED_LOCALES],
    load: 'languageOnly', // 'es' instead of 'es-MX'

    // Namespaces - preload common, navigation, and app for authenticated routes
    ns: [...NAMESPACES],
    defaultNS: 'common',
    preload: [...SUPPORTED_LOCALES],

    // Ensure these namespaces are loaded on init
    partialBundledLanguages: true,

    // Detection configuration
    detection: {
      order: ['path', 'localStorage', 'navigator'],
      lookupLocalStorage: 'settlewise_language',
      caches: ['localStorage'],
    },

    // Backend configuration for loading translations with retry on failure
    backend: {
      loadPath: '/locales/{{lng}}/{{ns}}.json',
      requestOptions: {
        cache: 'default',
      },
      // Custom request function with retry for truncated/failed responses
      request: (
        _options: unknown,
        url: string,
        _payload: unknown,
        callback: (err: unknown, result: { status: number; data: string }) => void,
      ) => {
        const attempt = (retries: number) => {
          fetch(url, { cache: 'default' })
            .then((res) => {
              if (!res.ok) throw new Error(`HTTP ${res.status}`);
              return res.text();
            })
            .then((text) => {
              // Validate JSON before returning to catch truncated responses
              JSON.parse(text);
              callback(null, { status: 200, data: text });
            })
            .catch((err) => {
              if (retries > 0) {
                setTimeout(() => attempt(retries - 1), 500);
              } else {
                callback(err, { status: 500, data: '' });
              }
            });
        };
        attempt(2); // Up to 2 retries (3 total attempts)
      },
    },

    // Interpolation settings
    interpolation: {
      escapeValue: false, // React already escapes values
    },

    // React settings
    react: {
      useSuspense: true,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
    },

    // Debug in development
    debug: import.meta.env.DEV,
  });

/**
 * Get the current locale from URL or default
 */
export function getCurrentLocale(): SupportedLocale {
  const pathParts = window.location.pathname.split('/').filter(Boolean);
  const firstPart = pathParts[0];

  if (firstPart && SUPPORTED_LOCALES.includes(firstPart as SupportedLocale)) {
    return firstPart as SupportedLocale;
  }
  return DEFAULT_LOCALE;
}

/**
 * Check if a locale is supported
 */
export function isValidLocale(locale: string): locale is SupportedLocale {
  return SUPPORTED_LOCALES.includes(locale as SupportedLocale);
}

/**
 * Build a localized path
 */
export function localizedPath(path: string, locale?: SupportedLocale): string {
  const targetLocale = locale || getCurrentLocale();
  const cleanPath = path.startsWith('/') ? path : `/${path}`;

  // Don't add prefix for default locale
  if (targetLocale === DEFAULT_LOCALE) {
    return cleanPath;
  }

  return `/${targetLocale}${cleanPath}`;
}

/**
 * Remove locale prefix from path
 */
export function stripLocaleFromPath(path: string): string {
  const pattern = new RegExp(`^/(${SUPPORTED_LOCALES.join('|')})`);
  return path.replace(pattern, '') || '/';
}

export default i18n;
